import { useEffect, useState } from 'react';
import { useUpdateScenarioCustomData } from '../hooks.js';

/**
 * Inline JSON editor for the per-scenario `custom-data` blob. The
 * engine treats custom-data as opaque JSON keyed by scenario; this
 * panel surfaces the current contents and lets a user replace them.
 *
 * Validation is "is it parseable JSON" — schema enforcement happens
 * engine-side. The UI prevents saving when the textarea isn't valid
 * JSON to avoid 400s on the round-trip.
 */
export function ScenarioCustomDataPanel({
  scenarioId,
  initialCustomData,
}: {
  scenarioId: string;
  initialCustomData: unknown;
}): JSX.Element {
  const { save, isPending, error } = useUpdateScenarioCustomData(scenarioId);
  const [draft, setDraft] = useState(() => formatJson(initialCustomData));
  const [parseError, setParseError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  // Reset draft when the scenario changes (or upstream payload refreshes).
  useEffect(() => {
    setDraft(formatJson(initialCustomData));
    setParseError(null);
    setSavedAt(null);
  }, [scenarioId, initialCustomData]);

  function validate(
    text: string,
  ): { ok: true; value: Record<string, unknown> } | { ok: false; error: string } {
    if (!text.trim()) return { ok: true, value: {} };
    try {
      const parsed = JSON.parse(text);
      if (parsed === null || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return { ok: false, error: 'Custom data must be a JSON object.' };
      }
      return { ok: true, value: parsed as Record<string, unknown> };
    } catch (e) {
      return { ok: false, error: e instanceof Error ? e.message : 'Invalid JSON' };
    }
  }

  async function submit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const v = validate(draft);
    if (!v.ok) {
      setParseError(v.error);
      return;
    }
    setParseError(null);
    await save(v.value);
    setSavedAt(Date.now());
  }

  return (
    <section className="mt-6 rounded-md border border-gray-200 dark:border-gray-700">
      <header className="border-b border-gray-200 bg-gray-50 px-4 py-2 text-sm font-semibold text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
        Custom data
      </header>
      <form onSubmit={submit} className="p-3">
        <textarea
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setParseError(null);
            setSavedAt(null);
          }}
          rows={8}
          spellCheck={false}
          className="w-full rounded-md border border-gray-300 bg-white px-2 py-1 font-mono text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
        />
        {parseError ? (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">{parseError}</p>
        ) : null}
        {error ? (
          <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error.message}</p>
        ) : null}
        {savedAt ? (
          <p className="mt-2 text-xs text-green-600 dark:text-green-400">Saved ✓</p>
        ) : null}
        <div className="mt-3 flex justify-end">
          <button
            type="submit"
            disabled={isPending}
            className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            {isPending ? 'Saving…' : 'Save custom data'}
          </button>
        </div>
      </form>
    </section>
  );
}

function formatJson(value: unknown): string {
  if (value === null || value === undefined) return '{}';
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '{}';
  }
}
